import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Activity,
  BrainCircuit,
  CheckCircle2,
  ChevronRight,
  Clock,
  HeartPulse,
  Moon,
  Pill,
  Sparkles,
  Target,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { usePatients } from "@/lib/usePatients";
import { buildClinicalIntelligenceSummary } from "@/domain/clinicalIntelligenceEngine";
import { buildBodyLoad } from "@/domain/pkBodyLoadEngine";
import { getRuntimeKnowledgeBase } from "@/lib/pharmacology/runtimeKnowledge";
import { SessionCheckin } from "@/components/clinical/SessionCheckin";
import { BodyLoadNowCard } from "@/components/clinical/BodyLoadNowCard";
import { normalizeVitalRecord } from "@/lib/clinical/vitalAssessment";
import { VitalSignsPanel } from "@/components/clinical/VitalSignsPanel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PATIENT_STORAGE_KEY = "clinicalos-my-space-patient";
const BODY_LOAD_HISTORY_DAYS = 120;

function formatDateTime(value: string | null | undefined) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function stateValue(checkin: any, key: string) {
  const value = checkin?.[key];
  return value == null ? null : Number(value);
}

function StateMetric({ label, value, inverse = false }: { label: string; value: number | null; inverse?: boolean }) {
  const display = value == null ? "—" : String(value);
  const normalized = value == null ? 0 : Math.max(0, Math.min(10, inverse ? 10 - value : value));
  return (
    <div className="min-w-0 rounded-2xl border bg-muted/25 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-end gap-1">
        <span className="text-2xl font-semibold tabular-nums">{display}</span>
        <span className="pb-0.5 text-xs text-muted-foreground">/10</span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${normalized * 10}%` }} />
      </div>
    </div>
  );
}

export default function MySpaceToday() {
  const { data: patients = [], isLoading: loadingPatients } = usePatients();
  const [patientId, setPatientId] = useState(() => localStorage.getItem(PATIENT_STORAGE_KEY) ?? "");
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [vitalsOpen, setVitalsOpen] = useState(false);

  useEffect(() => {
    if (!patients.length) return;
    const stillExists = patients.some((patient: any) => patient.id === patientId);
    if (stillExists) return;
    const active = patients.find((patient: any) => patient.status === "ativo") ?? patients[0];
    setPatientId(active.id);
    localStorage.setItem(PATIENT_STORAGE_KEY, active.id);
  }, [patients, patientId]);

  const patient = patients.find((item: any) => item.id === patientId) ?? null;
  const now = Date.now();
  const since14d = useMemo(() => new Date(now - 14 * 24 * 3600_000).toISOString(), [now]);
  const bodyLoadSince = useMemo(() => new Date(now - BODY_LOAD_HISTORY_DAYS * 24 * 3600_000).toISOString(), [now]);
  const knowledge = useMemo(() => getRuntimeKnowledgeBase(), []);
  const startToday = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date.toISOString();
  }, []);

  const { data: checkins = [], isLoading: loadingCheckins } = useQuery({
    queryKey: ["my-space-checkins", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("session_checkins")
        .select("*")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("checkin_at", since14d)
        .order("checkin_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: medications = [] } = useQuery({
    queryKey: ["my-space-medications", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("patient_medications")
        .select("*, substances(name), substance_formulations(formulation_name)")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .order("usual_time", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: doses = [] } = useQuery({
    queryKey: ["my-space-doses", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("medication_dose_logs")
        .select("*")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("actual_time", since14d)
        .order("actual_time", { ascending: false })
        .limit(300);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: bodyLoadDoses = [] } = useQuery({
    queryKey: ["recent-doses", patientId, "body-load"],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("medication_dose_logs")
        .select("*, substances(*), substance_formulations(*)")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("actual_time", bodyLoadSince)
        .order("actual_time", { ascending: false })
        .limit(1500);
      if (error) throw error;
      return data ?? [];
    },
    refetchInterval: 60_000,
  });

  const { data: vitals = [] } = useQuery({
    queryKey: ["my-space-vitals", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const canonical = await (supabase as any)
        .from("vital_signs")
        .select("*")
        .eq("patient_id", patientId)
        .order("measured_at", { ascending: false })
        .limit(30);
      const legacy = await (supabase as any)
        .from("patient_vital_signs")
        .select("*")
        .eq("patient_id", patientId)
        .order("measured_at", { ascending: false })
        .limit(30);

      if (canonical.error && legacy.error) throw canonical.error;
      return [
        ...((canonical.data ?? []) as any[])
          .filter((row) => !row.deleted_at)
          .map((row) => normalizeVitalRecord({ ...row, __source_table: "vital_signs" })),
        ...((legacy.data ?? []) as any[])
          .filter((row) => !row.deleted_at)
          .map((row) => normalizeVitalRecord({ ...row, __source_table: "patient_vital_signs" })),
      ].sort((a, b) => new Date(b.measured_at).getTime() - new Date(a.measured_at).getTime());
    },
  });

  const { data: sleepPeriods = [] } = useQuery({
    queryKey: ["my-space-sleep", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("sleep_periods")
        .select("*")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .order("sleep_start_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: targetSymptoms = [] } = useQuery({
    queryKey: ["my-space-target-symptoms", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("patient_target_symptoms")
        .select("*")
        .eq("patient_id", patientId)
        .eq("status", "ativo")
        .is("deleted_at", null)
        .order("priority", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: symptomMeasurements = [] } = useQuery({
    queryKey: ["my-space-symptom-measurements", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("symptom_measurements")
        .select("*")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("measured_at", since14d)
        .order("measured_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const latestCheckin = checkins[0] ?? null;
  const latestVital = vitals[0] ?? null;
  const latestSleep = sleepPeriods[0] ?? null;
  const activeMedications = medications.filter((medication: any) => !/suspens|descontinu|encerr/i.test(String(medication.status ?? "")));
  const bodyLoad = useMemo(
    () => buildBodyLoad(bodyLoadDoses as any[], { now: new Date(now), knowledge }),
    [bodyLoadDoses, now, knowledge],
  );

  const intelligence = useMemo(() => buildClinicalIntelligenceSummary({
    patient,
    medications: activeMedications,
    doses,
    checkins,
    targetSymptoms,
    symptomMeasurements,
    periodDays: 14,
  }), [patient, activeMedications, doses, checkins, targetSymptoms, symptomMeasurements]);

  const todayEvents = useMemo(() => {
    const events: Array<{ id: string; at: string; title: string; detail: string; kind: "dose" | "checkin" | "vital" | "sleep" }> = [];
    for (const dose of doses as any[]) {
      if (dose.actual_time < startToday) continue;
      events.push({
        id: `dose-${dose.id}`,
        at: dose.actual_time,
        title: `${dose.substance_name}${dose.dose_amount != null ? ` ${dose.dose_amount}${dose.dose_unit ? ` ${dose.dose_unit}` : ""}` : ""}`,
        detail: [dose.log_type, dose.formulation_name ?? dose.formulation, dose.route].filter(Boolean).join(" · ") || "dose registrada",
        kind: "dose",
      });
    }
    for (const checkin of checkins as any[]) {
      if (checkin.checkin_at < startToday) continue;
      events.push({
        id: `checkin-${checkin.id}`,
        at: checkin.checkin_at,
        title: "Check-in",
        detail: [
          checkin.mood_0_10 != null ? `humor ${checkin.mood_0_10}/10` : null,
          checkin.energy_0_10 != null ? `energia ${checkin.energy_0_10}/10` : null,
          checkin.anxiety_0_10 != null ? `ansiedade ${checkin.anxiety_0_10}/10` : null,
        ].filter(Boolean).join(" · ") || checkin.patient_report || "registro clínico",
        kind: "checkin",
      });
    }
    for (const vital of vitals as any[]) {
      if (vital.measured_at < startToday) continue;
      events.push({
        id: `vital-${vital.id}`,
        at: vital.measured_at,
        title: "Sinais vitais",
        detail: [
          vital.heart_rate_bpm != null ? `FC ${vital.heart_rate_bpm}` : null,
          vital.systolic_mm_hg != null && vital.diastolic_mm_hg != null ? `PA ${vital.systolic_mm_hg}/${vital.diastolic_mm_hg}` : null,
          vital.context,
        ].filter(Boolean).join(" · "),
        kind: "vital",
      });
    }
    for (const sleep of sleepPeriods as any[]) {
      if (sleep.sleep_start_at < startToday) continue;
      events.push({
        id: `sleep-${sleep.id}`,
        at: sleep.sleep_start_at,
        title: "Sono",
        detail: [sleep.duration_min != null ? `${Math.round(sleep.duration_min / 60 * 10) / 10} h` : null, sleep.quality_0_10 != null ? `qualidade ${sleep.quality_0_10}/10` : null].filter(Boolean).join(" · ") || "período de sono",
        kind: "sleep",
      });
    }
    return events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()).slice(0, 12);
  }, [doses, checkins, vitals, sleepPeriods, startToday]);

  const todayLabel = new Intl.DateTimeFormat("pt-BR", { weekday: "long", day: "2-digit", month: "long" }).format(new Date());
  const primaryInsight = intelligence.actionCards[0] ?? null;

  function selectPatient(next: string) {
    setPatientId(next);
    localStorage.setItem(PATIENT_STORAGE_KEY, next);
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 pb-4 sm:space-y-6">
      <section className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <div className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Meu Espaço</div>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">Hoje</h1>
            <p className="mt-1 text-sm text-muted-foreground">{todayLabel}</p>
          </div>
          <Button asChild variant="outline" className="w-full justify-between rounded-2xl sm:w-auto">
            <Link to="/inteligencia-clinica">
              Ir para Espaço Clínico
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>

        <Card className="p-3">
          <label htmlFor="my-space-patient" className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Perfil ativo</label>
          <select
            id="my-space-patient"
            className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 text-base"
            value={patientId}
            onChange={(event) => selectPatient(event.target.value)}
            disabled={loadingPatients}
          >
            <option value="">Selecione um paciente…</option>
            {patients.map((item: any) => <option key={item.id} value={item.id}>{item.social_name || item.full_name}</option>)}
          </select>
          <p className="mt-2 text-xs text-muted-foreground">O perfil escolhido controla check-ins, timeline, medicações e tendências exibidas nesta tela.</p>
        </Card>
      </section>

      {!patientId ? (
        <Card className="p-6 text-center text-sm text-muted-foreground">Selecione um perfil para iniciar.</Card>
      ) : (
        <>
          <Card className="overflow-hidden rounded-3xl border-primary/15 bg-gradient-to-br from-primary/10 via-card to-card shadow-sm">
            <div className="p-4 sm:p-6">
              <div className="flex items-start gap-3">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-sm">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-semibold">Agora</h2>
                    <Badge variant="secondary">dado real</Badge>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {latestCheckin
                      ? `Último check-in em ${formatDateTime(latestCheckin.checkin_at)}. Registre novamente quando houver mudança relevante ou para comparar fases da curva.`
                      : "Ainda não há check-in para este perfil. O primeiro registro habilita tendências e melhora a inteligência clínica."}
                  </p>
                  <Button className="mt-4 w-full rounded-2xl sm:w-auto" onClick={() => setCheckinOpen(true)}>
                    <Activity className="mr-2 h-4 w-4" />{latestCheckin ? "Registrar novo check-in" : "Fazer primeiro check-in"}
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          <Card className="rounded-3xl p-4 shadow-sm sm:p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold">Como o estado está</h2>
                <p className="text-xs text-muted-foreground">{loadingCheckins ? "Carregando…" : latestCheckin ? `medido ${formatDateTime(latestCheckin.checkin_at)}` : "sem medida ainda"}</p>
              </div>
              <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => setCheckinOpen(true)}>Editar</Button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <StateMetric label="Humor" value={stateValue(latestCheckin, "mood_0_10")} />
              <StateMetric label="Energia" value={stateValue(latestCheckin, "energy_0_10")} />
              <StateMetric label="Ansiedade" value={stateValue(latestCheckin, "anxiety_0_10")} inverse />
              <StateMetric label="Foco" value={stateValue(latestCheckin, "focus_0_10")} />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl bg-muted/40 px-3 py-2.5 text-sm">
              <Moon className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Sono mais recente:</span>
              <span className="font-medium">
                {latestSleep
                  ? `${latestSleep.duration_min != null ? `${Math.round(latestSleep.duration_min / 6) / 10} h` : "duração —"}${latestSleep.quality_0_10 != null ? ` · qualidade ${latestSleep.quality_0_10}/10` : ""}`
                  : latestCheckin?.sleep_quality_0_10 != null ? `qualidade ${latestCheckin.sleep_quality_0_10}/10` : "sem registro"}
              </span>
            </div>
          </Card>

          <BodyLoadNowCard result={bodyLoad} historyWindowDays={BODY_LOAD_HISTORY_DAYS} />

          <section className="grid gap-4 lg:grid-cols-2">
            <Card className="rounded-3xl p-4 shadow-sm sm:p-5">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                <h2 className="font-semibold">Evolução dos sintomas-alvo</h2>
              </div>
              <div className="mt-4 space-y-2">
                {intelligence.targetSymptomSignals.length ? intelligence.targetSymptomSignals.slice(0, 6).map((signal) => (
                  <div key={signal.id} className="rounded-2xl border p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="break-words text-sm font-medium">{signal.name}</div>
                        <div className="text-xs text-muted-foreground">{signal.measurementCount} medida(s) · atual {signal.current?.toFixed(1) ?? "—"}/10</div>
                      </div>
                      <Badge variant={signal.direction === "piorando" ? "destructive" : signal.direction === "melhorando" ? "default" : "secondary"}>{signal.direction}</Badge>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">Δ baseline {signal.deltaVsBaseline?.toFixed(1) ?? "—"} · próximo dado: {signal.nextData}</div>
                  </div>
                )) : (
                  <div className="text-sm text-muted-foreground">Defina sintomas-alvo e registre valores no check-in para gerar evolução automática.</div>
                )}
              </div>
            </Card>

            <Card className="rounded-3xl p-4 shadow-sm sm:p-5">
              <div className="flex items-center gap-2">
                <Pill className="h-5 w-5 text-primary" />
                <h2 className="font-semibold">Tratamento ativo</h2>
              </div>
              <div className="mt-4 space-y-2">
                {activeMedications.length ? activeMedications.slice(0, 8).map((medication: any) => {
                  const name = medication.substances?.name ?? medication.free_text_name ?? medication.brand_name ?? "Medicação";
                  const lastDose = (doses as any[]).find((dose) => dose.patient_medication_id === medication.id || (dose.substance_id && dose.substance_id === medication.substance_id));
                  return (
                    <div key={medication.id} className="rounded-2xl border p-3">
                      <div className="font-medium">{name}</div>
                      <div className="text-xs text-muted-foreground">
                        {[medication.current_dose != null ? `${medication.current_dose} ${medication.dose_unit ?? ""}`.trim() : null, medication.frequency, medication.usual_time ? String(medication.usual_time).slice(0, 5) : null].filter(Boolean).join(" · ") || "esquema sem detalhes"}
                      </div>
                      <div className="mt-1 text-[11px] text-muted-foreground">{lastDose ? `última dose ${formatDateTime(lastDose.actual_time)}` : "sem dose registrada no período"}</div>
                    </div>
                  );
                }) : <div className="text-sm text-muted-foreground">Nenhum tratamento ativo cadastrado.</div>}
              </div>
            </Card>
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <Card className="rounded-3xl p-4 shadow-sm sm:p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <HeartPulse className="h-5 w-5 text-primary" />
                  <h2 className="font-semibold">Sinais vitais</h2>
                </div>
                <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => setVitalsOpen(true)}>
                  Registrar PA/FC
                </Button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="rounded-2xl border bg-muted/30 p-3">
                  <div className="text-xs text-muted-foreground">Última FC</div>
                  <div className="mt-1 text-xl font-semibold tabular-nums">{latestVital?.heart_rate_bpm != null ? `${latestVital.heart_rate_bpm} bpm` : "—"}</div>
                </div>
                <div className="rounded-2xl border bg-muted/30 p-3">
                  <div className="text-xs text-muted-foreground">Última PA</div>
                  <div className="mt-1 text-xl font-semibold tabular-nums">{latestVital?.systolic_mm_hg != null && latestVital?.diastolic_mm_hg != null ? `${latestVital.systolic_mm_hg}/${latestVital.diastolic_mm_hg}` : "—"}</div>
                </div>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">{latestVital ? `${formatDateTime(latestVital.measured_at)}${latestVital.context ? ` · ${latestVital.context}` : ""}` : "Sem medida registrada."}</div>
            </Card>

            <Card className="rounded-3xl border-primary/15 p-4 shadow-sm sm:p-5">
              <div className="flex items-start gap-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
                  <BrainCircuit className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-semibold">Leitura clínica</h2>
                    <Badge variant="outline">qualidade {intelligence.dataQualityScore}/100</Badge>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {primaryInsight?.rationale ?? "Ainda faltam dados suficientes para uma hipótese longitudinal. Faça check-ins e registre doses, sono e contexto."}
                  </p>
                  {primaryInsight ? <div className="mt-3 rounded-2xl bg-muted/40 p-3 text-xs"><span className="font-medium">Próximo passo para revisão médica:</span> {primaryInsight.action}</div> : null}
                </div>
              </div>
            </Card>
          </section>

          <Card className="rounded-3xl p-4 shadow-sm sm:p-5">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              <h2 className="font-semibold">Linha do tempo de hoje</h2>
            </div>
            <div className="mt-4 space-y-3">
              {todayEvents.length ? todayEvents.map((event) => {
                const Icon = event.kind === "dose" ? Pill : event.kind === "vital" ? HeartPulse : event.kind === "sleep" ? Moon : Activity;
                return (
                  <div key={event.id} className="flex min-w-0 gap-3">
                    <div className="w-12 shrink-0 pt-1 text-xs font-medium tabular-nums text-muted-foreground">
                      {new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(new Date(event.at))}
                    </div>
                    <div className="flex min-w-0 flex-1 gap-3 border-b pb-3 last:border-0">
                      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-muted text-muted-foreground"><Icon className="h-4 w-4" /></div>
                      <div className="min-w-0 flex-1">
                        <div className="break-words text-sm font-medium">{event.title}</div>
                        <div className="break-words text-xs text-muted-foreground">{event.detail}</div>
                      </div>
                    </div>
                  </div>
                );
              }) : <div className="text-sm text-muted-foreground">Nenhum evento registrado hoje.</div>}
            </div>
          </Card>
        </>
      )}

      <Dialog open={vitalsOpen} onOpenChange={setVitalsOpen}>
        <DialogContent className="max-h-[94dvh] overflow-y-auto p-0 sm:max-w-5xl">
          <DialogHeader className="border-b px-4 pb-3 pt-4 sm:px-5">
            <DialogTitle>Registrar PA / FC · {patient?.social_name || patient?.full_name || "paciente"}</DialogTitle>
          </DialogHeader>
          {patientId ? (
            <div className="p-3 sm:p-4">
              <VitalSignsPanel patientId={patientId} onSaved={() => setVitalsOpen(false)} />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={checkinOpen} onOpenChange={setCheckinOpen}>
        <DialogContent className="p-0 sm:max-w-3xl">
          <DialogHeader className="border-b px-4 pb-3 pt-4 sm:px-5">
            <DialogTitle>Registrar check-in · {patient?.social_name || patient?.full_name || "paciente"}</DialogTitle>
          </DialogHeader>
          {patientId ? <div className="max-h-[calc(100dvh-7rem)] overflow-y-auto p-3 sm:p-4"><SessionCheckin patientId={patientId} onSaved={() => setCheckinOpen(false)} /></div> : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
